import reduceSourceCode from './shaders/hierarchical-depth-reduce-source.comp.wgsl';
import reduceSourceMSAACode from './shaders/hierarchical-depth-reduce-source-msaa.comp.wgsl';
import reduceCode from './shaders/hierarchical-depth-reduce.comp.wgsl';
import reduceShmemCode from './shaders/hierarchical-depth-reduce-shmem.wgsl';
import reduceBodyCode from './shaders/hierarchical-depth-reduce-body.wgsl';
import { $wgsl } from '../wgsl-preprocessor/wgsl-preprocessor';
import { GpuPassTimer } from './utils/GpuPassTimer';

/**
 * Builds a hierarchical depth buffer (HZB) as a mip pyramid of per-region min and max reverse-z values using
 * shared-memory tile-reduce compute dispatches. R = min (conservative far bound), G = max (closest occluder).
 * See the algorithm and design rationale in src/wgs/wgpu/shaders/hierarchical-depth-reduce.md.
 *
 * Three pipelines cover the chain, i.e., reduceSource, reduceSourceMSAA, and reduce. The WG_SIZE override selects
 * wg32 (6 mips/dispatch, preferred) or wg16 (5 mips/dispatch, fallback) at create() time based on device limits.
 * Use setMipLevelCap() to tune chain depth and dispatch count.
 *
 * @example
 *   const hzb = new HierarchicalDepthPass(device);
 *   hzb.create();
 *   hzb.update(depthTexture); // call each frame before run(); also handles resize
 *   hzb.run();
 *   const view = hzb.getDepthTextureView();
 *
 * @todo When WGSL `subgroups` ships in core (currently chromium-experimental), replace the in-shmem reduction with
 *   subgroupMin + subgroupMax + subgroupBroadcast. Track Chrome release notes and the gpuweb spec.
 * @todo Implement GPU-side culling helpers, e.g., project AABB, select mip, and cull test.
 * @todo Implement GPU-side ray march helper, e.g., for SSR.
 */
export class HierarchicalDepthPass {

  // Number of output storage texture bindings in every bind group, i.e., always 6. wg32 uses all 6 (5 reductions);
  // wg16 uses 5, with output5 bound to a dummy since stride5 = WG_SIZE >> 5 = 0 makes that reduction block dead in
  // the shader. Single layout for both variants.
  static #LAYOUT_MIPS = 6;

  // Returns the bind group layout output entries (LAYOUT_MIPS write-only rg32float storage textures at consecutive
  // bindings 0..LAYOUT_MIPS - 1). Shared by all three pipeline setup paths.
  static #makeOutputEntries() {
    const layoutMips = HierarchicalDepthPass.#LAYOUT_MIPS;
    const entries = new Array(layoutMips);
    const storageTexture = { access: 'write-only', format: 'rg32float' };
    for (let binding = 0; binding < layoutMips; ++binding) {
      entries[binding] = { binding, visibility: GPUShaderStage.COMPUTE, storageTexture };
    }
    return entries;
  }

  // Maximum number of mip levels to emit. The natural chain depth is capped to this value; reduce it to
  // limit the chain to one dispatch (e.g. setMipLevelCap(5) for HD). Infinity = full natural chain.
  static DEFAULT_MIP_LEVEL_CAP = Infinity;

  #device;

  // Selected at create() based on device limits. 32 (preferred wg32) or 16 (wg16 fallback).
  #workgroupSize;
  // Mips emitted per dispatch: log2(#workgroupSize) + 1 = 6 for wg32, 5 for wg16.
  #mipsPerPass;

  #reduceSourcePipeline;
  #reduceSourceMSAAPipeline;
  #reducePipeline;

  #reduceSourceLayout;
  #reduceSourceMSAALayout;
  #reduceLayout;

  #depthView;
  #depthIsMSAA = false;
  #hzTexture;
  #hzView;
  #hzViews;
  #hzSampler;
  #dummyTextures;
  #dummyViews;

  #mipLevelCap = HierarchicalDepthPass.DEFAULT_MIP_LEVEL_CAP;
  // Set by setMipLevelCap() to force #resize() on the next update() even when viewport dims unchanged.
  #needsRebuild = false;
  // Last depth source passed to update(). Used to rebuild synchronously from setMipLevelCap().
  #lastSource = null;

  // [{ pipeline, bindGroup, dispatch: [x, y, 1] }] - one entry per tile-reduce dispatch.
  #passes;

  // Optional GPU timing. Off by default; call setTimingEnabled(true, viewer) to activate.
  #timer = null;

  // True once create() has run; MSAA and reduce pipelines compile lazily on first use inside update().
  #ready() {return this.#device && this.#reduceSourcePipeline;}

  constructor(device) {
    this.#device = device;
  }

  /**
   * Creates the compute pipelines, sampler, and dummy storage textures. Idempotent. Bails (with a
   * warning) on devices below the minimum storage-texture limit -- in that case the pass stays in an
   * unready state and update()/run() become no-ops. Picks WG_SIZE = 32 or 16 at pipeline creation
   * via override constants based on actual device limits.
   */
  create() {
    if (this.#ready()) return;

    const limits = this.#device.limits;

    if (limits.maxStorageTexturesPerShaderStage < HierarchicalDepthPass.#LAYOUT_MIPS) {
      console.warn(`HierarchicalDepthPass: device only supports ${limits.maxStorageTexturesPerShaderStage} ` +
      `storage textures per stage; HZB requires >= ${HierarchicalDepthPass.#LAYOUT_MIPS}. HZB disabled.`);
      return;
    }

    this.#workgroupSize = limits.maxComputeInvocationsPerWorkgroup >= 1024 ? 32 : 16;
    this.#mipsPerPass = Math.floor(Math.log2(this.#workgroupSize)) + 1; // 6 for wg32, 5 for wg16

    const layoutMips = HierarchicalDepthPass.#LAYOUT_MIPS;
    const inputBinding = layoutMips;
    const samplerBinding = layoutMips + 1;

    this.#reduceSourceLayout = this.#device.createBindGroupLayout({
      label: 'hierarchical-depth reduce-source bind group layout',
      entries: [
      ...HierarchicalDepthPass.#makeOutputEntries(),
      { binding: inputBinding, visibility: GPUShaderStage.COMPUTE, texture: { sampleType: 'depth' } },
      { binding: samplerBinding, visibility: GPUShaderStage.COMPUTE, sampler: { type: 'non-filtering' } }]

    });
    // The HZB-to-HZB reduce pipeline (pass 1+) is compiled lazily in #ensureReducePipeline(). It is only needed
    // when mipLevelCount > #mipsPerPass, which may never occur for small mipLevelCap values or low-resolution
    // targets. Deferring avoids the compilation cost on weak devices.

    // Default sampler: nearest + clamp-to-edge. Clamp-to-edge is load-bearing -- gather UV at the pixel boundary
    // clamps OOB samples to the edge pixel, giving correct coverage for odd dimensions without any explicit edge
    // handling in the shaders.
    this.#hzSampler = this.#device.createSampler({ label: 'hierarchical-depth sampler' });

    // Dummy 1x1 rg32float textures for unused output slots in the trailing pass. Writable storage bindings within
    // a single bind group cannot alias the same subresource, so each unused slot needs its own texture. At most
    // #LAYOUT_MIPS - 1 slots can be unused (when only one real mip remains in the trailing pass). Writes from
    // threads with coordinates >= 1 land OOB (no-op); thread (0,0) of WG (0,0) overwrites cell [0,0] -- harmless,
    // we never read the dummies.
    const dummyCount = HierarchicalDepthPass.#LAYOUT_MIPS - 1;
    this.#dummyTextures = new Array(dummyCount);
    this.#dummyViews = new Array(dummyCount);
    const dummyAttributes = { dimension: '2d', size: [1, 1, 1], format: 'rg32float', usage: GPUTextureUsage.STORAGE_BINDING };
    for (let i = 0; i < dummyCount; ++i) {
      this.#dummyTextures[i] = this.#device.createTexture({ label: `hierarchical-depth dummy ${i}`, ...dummyAttributes });
      this.#dummyViews[i] = this.#dummyTextures[i].createView({ label: `hierarchical-depth dummy view ${i}` });
    }

    const overrideConstants = { WG_SIZE: this.#workgroupSize };

    this.#reduceSourcePipeline = this.#device.createComputePipeline({
      layout: this.#device.createPipelineLayout({ bindGroupLayouts: [this.#reduceSourceLayout] }),
      compute: {
        module: this.#device.createShaderModule({
          label: 'hierarchical-depth reduce-source',
          code: $wgsl(reduceSourceCode, { reduceShmemCode, reduceBodyCode })
        }),
        entryPoint: 'main',
        constants: overrideConstants
      }
    });

  }

  #ensureMSAAPipeline() {
    if (this.#reduceSourceMSAAPipeline) return;

    this.#reduceSourceMSAALayout = this.#device.createBindGroupLayout({
      label: 'hierarchical-depth reduce-source msaa bind group layout',
      entries: [
      ...HierarchicalDepthPass.#makeOutputEntries(),
      {
        binding: HierarchicalDepthPass.#LAYOUT_MIPS,
        visibility: GPUShaderStage.COMPUTE,
        texture: { sampleType: 'depth', multisampled: true }
      }]

    });
    this.#reduceSourceMSAAPipeline = this.#device.createComputePipeline({
      layout: this.#device.createPipelineLayout({ bindGroupLayouts: [this.#reduceSourceMSAALayout] }),
      compute: {
        module: this.#device.createShaderModule({
          label: 'hierarchical-depth reduce-source msaa',
          code: $wgsl(reduceSourceMSAACode, { reduceShmemCode, reduceBodyCode })
        }),
        entryPoint: 'main',
        constants: { WG_SIZE: this.#workgroupSize }
      }
    });
  }

  // Compiled lazily on the first dispatch that needs more than one pass (mipLevelCount > #mipsPerPass).
  // Devices operating with a small mipLevelCap or low-resolution targets never need this pipeline.
  #ensureReducePipeline() {
    if (this.#reducePipeline) return;

    const layoutMips = HierarchicalDepthPass.#LAYOUT_MIPS;
    const inputBinding = layoutMips;
    const samplerBinding = layoutMips + 1;

    this.#reduceLayout = this.#device.createBindGroupLayout({
      label: 'hierarchical-depth reduce bind group layout',
      entries: [
      ...HierarchicalDepthPass.#makeOutputEntries(),
      { binding: inputBinding, visibility: GPUShaderStage.COMPUTE, texture: { sampleType: 'unfilterable-float' } },
      { binding: samplerBinding, visibility: GPUShaderStage.COMPUTE, sampler: { type: 'non-filtering' } }]

    });
    this.#reducePipeline = this.#device.createComputePipeline({
      layout: this.#device.createPipelineLayout({ bindGroupLayouts: [this.#reduceLayout] }),
      compute: {
        module: this.#device.createShaderModule({
          label: 'hierarchical-depth reduce',
          code: $wgsl(reduceCode, { reduceShmemCode, reduceBodyCode })
        }),
        entryPoint: 'main',
        constants: { WG_SIZE: this.#workgroupSize }
      }
    });
  }

  #resize(width, height) {
    if (!this.#ready()) return;

    // Ceil division ensures every source pixel is covered by at least one HZB texel, even for odd
    // source dimensions. WebGPU uses floor division for subsequent mips internally.
    const mipZeroSize = [Math.max(1, Math.ceil(width / 2)), Math.max(1, Math.ceil(height / 2))];

    // Natural chain: halve each level until the smaller dim reaches 1. Then cap to #mipLevelCap.
    // The deepest mip may be tile-aliased (tile-local max rather than global max) when the dispatch
    // WG count exceeds the mip dim -- safe for occlusion culling (conservative-low). See the .md doc.
    const minDim = Math.min(mipZeroSize[0], mipZeroSize[1]);
    const naturalMipCount = Math.max(1, 1 + Math.floor(Math.log2(minDim)));
    const mipLevelCount = Math.max(1, Math.min(this.#mipLevelCap, naturalMipCount));

    this.#hzTexture?.destroy();
    this.#hzTexture = this.#device.createTexture({
      label: 'hierarchical-depth buffer',
      dimension: '2d', size: mipZeroSize, mipLevelCount, format: 'rg32float',
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.STORAGE_BINDING
    });

    this.#hzView = this.#hzTexture.createView({ label: 'hierarchical-depth full view' });

    this.#hzViews = new Array(mipLevelCount);
    for (let level = 0; level < mipLevelCount; ++level) {
      this.#hzViews[level] = this.#hzTexture.createView({
        label: `hierarchical-depth mip ${level}`,
        baseMipLevel: level, mipLevelCount: 1
      });
    }
  }

  destroy() {
    this.#reduceSourcePipeline = this.#reduceSourceMSAAPipeline = this.#reducePipeline = undefined;
    this.#reduceSourceLayout = this.#reduceSourceMSAALayout = this.#reduceLayout = undefined;

    this.#dummyTextures?.forEach((t) => t.destroy());
    this.#dummyTextures = this.#dummyViews = undefined;

    this.#hzTexture?.destroy();
    this.#hzTexture = this.#hzView = this.#hzViews = this.#hzSampler = undefined;

    this.#depthView = this.#lastSource = undefined;
    this.#depthIsMSAA = false;

    this.#passes = undefined;
    this.#timer?.destroy();
    this.#timer = null;
  }

  /**
   * Caps the number of mip levels in the HZB pyramid. Use to limit the chain to a single dispatch, e.g.,
   * setMipLevelCap(5), or restore the full natural chain (setMipLevelCap(Infinity)). The natural chain depth is
   * log2(min source dim / 2), typically 9-10 for HD. Triggers a rebuild on the next update().
   *
   * @param {number} n - Maximum mip level count. Infinity = full natural chain (default).
   */
  setMipLevelCap(n) {
    if (this.#mipLevelCap === n) return;
    this.#mipLevelCap = n;
    this.#needsRebuild = true;
    this.#passes = undefined;

    // If we already have a source texture from a previous update(), rebuild immediately so the
    // inspector and timing keep working even when the scene is idle (no pending render tick).
    // If not, the rebuild happens on the next computeHZB() -> update() call.
    if (this.#lastSource && this.#ready()) this.update(this.#lastSource);
  }

  /** @returns {number} Configured cap (may be Infinity). Use getEffectiveMipLevelCount() for the resolved value. */
  getMipLevelCap() {return this.#mipLevelCap;}

  /** @returns {number} Mip level count at the current resolution after applying the cap. 0 before first update(). */
  getEffectiveMipLevelCount() {return this.#hzTexture?.mipLevelCount ?? 0;}

  /** @returns {number} Selected workgroup size per dimension (16 or 32). */
  getWorkgroupSize() {return this.#workgroupSize;}

  /** @returns {number} Mips emitted per dispatch for the selected variant (6 for wg32, 5 for wg16). */
  getMipsPerPass() {return this.#mipsPerPass;}

  /**
   * Attaches a new source depth texture, resizes the HZB pyramid if dimensions changed, and rebuilds the bind groups
   * for each tile-reduce dispatch. Must be called before run() and after any resize and depth-pass write.
   *
   * @param {GPUTexture} source - Depth texture (format must start with 'depth'). May be 4x MSAA.
   */
  update(source) {
    if (!this.#ready() || !source) return;

    if (!source.format.startsWith('depth')) {
      console.warn(`HierarchicalDepthPass: expected depth format, got '${source.format}'`);
      return;
    }

    const isMSAA = source.sampleCount > 1;

    // Hot path: source texture identity, MSAA flag, and cap are all stable -- nothing to rebuild.
    // The depth texture's contents change every frame but the handle and view are reusable.
    if (source === this.#lastSource && !this.#needsRebuild && this.#passes && isMSAA === this.#depthIsMSAA) {
      return;
    }
    this.#lastSource = source;

    if (isMSAA) this.#ensureMSAAPipeline();

    this.#depthView = source.createView({ label: 'hierarchical-depth source view' });

    const { width: currentWidth, height: currentHeight } = this.#hzTexture ?? {};
    const needsResize = this.#needsRebuild ||
    Math.ceil(source.width / 2) !== currentWidth ||
    Math.ceil(source.height / 2) !== currentHeight;
    this.#needsRebuild = false;
    needsResize && this.#resize(source.width, source.height);

    this.#depthIsMSAA = isMSAA;

    this.#buildPasses();
  }

  // Returns the 6 output views for a dispatch: real HZB mip views where the pass writes actual data,
  // dummy 1x1 views for any remaining slots the shader leaves dead (trailing pass or wg16 slot 5).
  #passOutputs(passIndex) {
    const start = passIndex * this.#mipsPerPass;
    const layoutMips = HierarchicalDepthPass.#LAYOUT_MIPS;
    const out = new Array(layoutMips);
    let dummyIndex = 0;
    for (let s = 0; s < layoutMips; ++s) {
      const mipLevel = start + s;
      // Only real mip if within this variant's per-pass mip count AND within the allocated chain.
      // For wg16 s >= 5 is always a dummy (stride5 = 0 -> shader never writes output5).
      if (s < this.#mipsPerPass && mipLevel < this.#hzViews.length) {
        out[s] = this.#hzViews[mipLevel];
      } else {
        out[s] = this.#dummyViews[dummyIndex++];
      }
    }
    return out;
  }

  #outputBindGroupEntries(outputs) {
    const layoutMips = HierarchicalDepthPass.#LAYOUT_MIPS;
    const entries = new Array(layoutMips);
    for (let i = 0; i < layoutMips; ++i) {
      entries[i] = { binding: i, resource: outputs[i] };
    }
    return entries;
  }

  #makePass0BindGroup() {
    const outputs = this.#passOutputs(0);
    const entries = this.#outputBindGroupEntries(outputs);
    const layoutMips = HierarchicalDepthPass.#LAYOUT_MIPS;
    entries.push({ binding: layoutMips, resource: this.#depthView });

    if (this.#depthIsMSAA) {
      return this.#device.createBindGroup({
        label: 'hierarchical-depth pass 0 (msaa)', layout: this.#reduceSourceMSAALayout, entries
      });
    }

    entries.push({ binding: layoutMips + 1, resource: this.#hzSampler });
    return this.#device.createBindGroup({
      label: 'hierarchical-depth pass 0', layout: this.#reduceSourceLayout, entries
    });
  }

  #buildPasses() {
    const wgSize = this.#workgroupSize;
    const passCount = Math.ceil(this.#hzViews.length / this.#mipsPerPass);

    if (passCount > 1) this.#ensureReducePipeline();

    this.#passes = new Array(passCount);

    for (let p = 0; p < passCount; ++p) {
      const firstOutputMip = p * this.#mipsPerPass;

      // Dispatch grid is sized to cover the first output mip in workgroupSize-wide tiles. Each WG processes a
      // (2 * workgroupSize)-wide input region (= workgroupSize-wide output region). For non-power-of-2 input
      // dimensions the rightmost / bottommost rows of the input may not be aggregated into the deeper emitted
      // mips; this matches WebGPU's standard floor-division mip semantics and is safe for occlusion culling.
      const mipWidth = Math.max(1, this.#hzTexture.width >>> firstOutputMip);
      const mipHeight = Math.max(1, this.#hzTexture.height >>> firstOutputMip);
      const dispatch = [Math.ceil(mipWidth / wgSize), Math.ceil(mipHeight / wgSize), 1];

      if (p === 0) {
        this.#passes[p] = {
          pipeline: this.#depthIsMSAA ? this.#reduceSourceMSAAPipeline : this.#reduceSourcePipeline,
          bindGroup: this.#makePass0BindGroup(), dispatch
        };
        continue;
      }

      const outputs = this.#passOutputs(p);
      const entries = this.#outputBindGroupEntries(outputs);
      const layoutMips = HierarchicalDepthPass.#LAYOUT_MIPS;
      entries.push({ binding: layoutMips + 0, resource: this.#hzViews[firstOutputMip - 1] });
      entries.push({ binding: layoutMips + 1, resource: this.#hzSampler });

      this.#passes[p] = {
        pipeline: this.#reducePipeline,
        bindGroup: this.#device.createBindGroup({
          label: `hierarchical-depth pass ${p}`,
          layout: this.#reduceLayout, entries
        }),
        dispatch
      };
    }
  }

  /**
   * Submits the HZB compute work as a single compute pass with one dispatch per tile-reduce stage.
   * WebGPU guarantees sequential execution of dispatches within a compute pass for storage textures,
   * so no explicit barriers are needed between the reduce stages.
   */
  run() {
    if (!this.#ready() || !this.#passes) return;

    const capture = this.#timer?.available ?? false;

    const encoder = this.#device.createCommandEncoder({ label: 'hierarchical-depth encoder' });
    const passDesc = { label: 'hierarchical-depth pass' };
    if (capture) this.#timer.inject(passDesc);

    const pass = encoder.beginComputePass(passDesc); // nosemgrep

    for (let i = 0; i < this.#passes.length; ++i) {
      const stage = this.#passes[i];
      pass.setPipeline(stage.pipeline);
      pass.setBindGroup(0, stage.bindGroup);
      pass.dispatchWorkgroups(...stage.dispatch);
    }

    pass.end();

    if (capture) this.#timer.resolve(encoder);
    this.#device.queue.submit([encoder.finish()]);
    if (capture) this.#timer.readback();
  }

  /** @returns {GPUTexture} */
  getDepthTexture() {return this.#hzTexture;}

  /** @returns {GPUTextureView} Full view spanning all mip levels. */
  getDepthTextureView() {return this.#hzView;}

  /** Enables or disables per-dispatch GPU timing. No-op if timestamp-query is unavailable. */
  setTimingEnabled(enabled) {
    if (enabled === (this.#timer !== null)) return;

    this.#timer?.destroy();
    this.#timer = enabled ? new GpuPassTimer(this.#device, 'HierarchicalDepthPass') : null;
  }

  /** @returns {boolean} True when timing is active. */
  isTimingEnabled() {return this.#timer !== null;}
}